import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import type { ForecastFormValues } from './forecast-form-model';
import type { ForecastFormController } from './useForecastForm';

type TextFieldKey = 'competenceMonth' | 'amount' | 'category' | 'description';

type ForecastTextFieldProps = {
  form: ForecastFormController;
  field: TextFieldKey;
  id: string;
  placeholder?: string;
  inputMode?: 'decimal';
};

function ForecastTextField({ form, field, id, placeholder, inputMode }: ForecastTextFieldProps) {
  const { t } = useTranslation('finance');
  const error = form.errors[field];

  return (
    <div className="space-y-2">
      <label className="field-label" htmlFor={id}>
        {t(`dashboard.forecast.fields.${field}`)}
      </label>
      <Input
        id={id}
        placeholder={placeholder}
        inputMode={inputMode}
        value={form.values[field]}
        aria-invalid={error ? true : undefined}
        onChange={(event) => form.setField(field, event.target.value)}
      />
      {error && <FormFeedback tone="error">{error}</FormFeedback>}
    </div>
  );
}

function DirectionFieldset({ form }: { form: ForecastFormController }) {
  const { t } = useTranslation('finance');

  return (
    <fieldset className="space-y-2">
      <legend className="field-label">{t('dashboard.forecast.fields.direction')}</legend>
      <div className="flex flex-wrap gap-4">
        {(['inflow', 'outflow'] as const).map((value) => (
          <label key={value} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="finance-forecast-direction"
              checked={form.values.direction === value}
              onChange={() => form.setField('direction', value)}
            />
            {t(`dashboard.directionValues.${value}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ForecastForm({ form }: { form: ForecastFormController }) {
  const { t } = useTranslation('finance');
  const values: ForecastFormValues = form.values;

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit();
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <ForecastTextField
          form={form}
          field="competenceMonth"
          id="finance-forecast-month"
          placeholder="YYYY-MM"
        />
        <ForecastTextField
          form={form}
          field="amount"
          id="finance-forecast-amount"
          inputMode="decimal"
        />
      </div>

      <DirectionFieldset form={form} />

      <div className="grid gap-3 md:grid-cols-2">
        <ForecastTextField form={form} field="category" id="finance-forecast-category" />
        <ForecastTextField form={form} field="description" id="finance-forecast-description" />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={values.economicRelevant}
          onChange={(event) => form.setField('economicRelevant', event.target.checked)}
        />
        {t('dashboard.forecast.fields.economicRelevant')}
      </label>

      {form.apiError && <FormFeedback tone="error">{form.apiError}</FormFeedback>}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={form.pending}>
          {t('dashboard.forecast.save')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={form.pending}
          onClick={form.reset}
        >
          {t('dashboard.forecast.cancel')}
        </Button>
      </div>
    </form>
  );
}

export function ForecastFormPanel({ form }: { form: ForecastFormController }): ReactNode {
  const { t } = useTranslation('finance');

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="card-detail">{t('dashboard.forecast.title')}</p>
        <Button type="button" size="sm" onClick={form.openCreate}>
          {t('dashboard.forecast.new')}
        </Button>
      </div>

      {/* Outside the form on purpose. A successful write sets `forecastSaved` and
          closes the form in the same continuation, and React batches both, so no
          render ever observed `forecastSaved === true` while the form was still
          mounted — the confirmation could never appear and the `dashboard.forecast
          .saved` key was dead in both locales, leaving screen-reader users with no
          announcement that the write landed (SL-0053 review, console finding 3). */}
      {form.saved && <FormFeedback tone="success">{t('dashboard.forecast.saved')}</FormFeedback>}

      {form.mode !== null && <ForecastForm form={form} />}
    </div>
  );
}
